import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Lightbulb, Sparkles, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const RequestBlog = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    requester_name: "",
    requester_email: "",
    topic: "",
    description: "",
    category_preference: "",
    urgency: "medium",
    website: "", // honeypot
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { error } = await supabase.functions.invoke("submit-blog-request", {
        body: formData,
      });

      if (error) throw error;

      toast({
        title: "Request submitted successfully!",
        description: "Thank you for your suggestion! We'll review your request and consider it for future content.",
        duration: 5000,
      });

      // Reset form
      setFormData({
        requester_name: "",
        requester_email: "",
        topic: "",
        description: "",
        category_preference: "",
        urgency: "medium",
        website: "",
      });
    } catch (error: any) {
      console.error("Error submitting blog request:", error);
      toast({
        title: "Failed to submit request",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Request a Blog Post | AI Blog</title>
        <meta
          name="description"
          content="Have a topic you'd like us to cover? Request a blog post and help shape our content."
        />
      </Helmet>

      <div className="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <Lightbulb className="mx-auto h-12 w-12 text-primary mb-4" />
            <h1 className="text-4xl font-bold text-foreground mb-4">
              Request a Blog Post
            </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Have a topic you'd like us to cover? Share your ideas and help shape our content!
          </p>
        </div>

        <Link to="/" className="inline-block mb-6">
          <Button variant="ghost" size="sm" className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Blog
          </Button>
        </Link>

        <div className="bg-card rounded-lg shadow-lg p-8 border border-border">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Honeypot field - hidden from users */}
              <input
                type="text"
                name="website"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="hidden"
                tabIndex={-1}
                autoComplete="off"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="requester_name">Your Name (Optional)</Label>
                  <Input
                    id="requester_name"
                    type="text"
                    value={formData.requester_name}
                    onChange={(e) => setFormData({ ...formData, requester_name: e.target.value })}
                    placeholder="John Doe"
                    maxLength={100}
                  />
                </div>

                <div>
                  <Label htmlFor="requester_email">
                    Email <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="requester_email"
                    type="email"
                    value={formData.requester_email}
                    onChange={(e) => setFormData({ ...formData, requester_email: e.target.value })}
                    placeholder="john@example.com"
                    required
                    maxLength={255}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="topic">
                  Topic / Title <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="topic"
                  type="text"
                  value={formData.topic}
                  onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                  placeholder="e.g., How AI is transforming healthcare"
                  required
                  maxLength={200}
                />
                <p className="text-sm text-muted-foreground mt-1">
                  {formData.topic.length}/200 characters
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="category_preference">Suggested Category (Optional)</Label>
                  <Input
                    id="category_preference"
                    type="text"
                    value={formData.category_preference}
                    onChange={(e) => setFormData({ ...formData, category_preference: e.target.value })}
                    placeholder="e.g., AI Ethics, Technology"
                    maxLength={100}
                  />
                </div>

                <div>
                  <Label htmlFor="urgency">Urgency</Label>
                  <Select
                    value={formData.urgency}
                    onValueChange={(value) => setFormData({ ...formData, urgency: value })}
                  >
                    <SelectTrigger id="urgency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low - Nice to have</SelectItem>
                      <SelectItem value="medium">Medium - Would appreciate</SelectItem>
                      <SelectItem value="high">High - Really need this</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="description">
                  Why is this topic important? <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Tell us why you'd like to see this topic covered. What specific aspects interest you?"
                  required
                  maxLength={1500}
                  rows={6}
                  className="resize-none"
                />
                <p className="text-sm text-muted-foreground mt-1">
                  {formData.description.length}/1500 characters
                </p>
              </div>

              <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
                <Sparkles className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  <p className="font-medium text-foreground mb-1">What makes a great request?</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Clear and specific topic</li>
                    <li>Explain why it's valuable to you and others</li>
                    <li>Mention any specific questions you'd like answered</li>
                    <li>Check if we've already covered similar topics</li>
                  </ul>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting || !formData.requester_email || !formData.topic || !formData.description}
              >
                {isSubmitting ? "Submitting..." : "Submit Request"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default RequestBlog;
